/**
 * LMS Progress Tracking Service
 * Tracks student progress, completion status, and learning analytics
 */

import axios from 'axios';
import { getApiBaseUrl } from './httpClient';

const API_BASE_URL = getApiBaseUrl();

const normalizeStudentPhone = (phone) =>
  String(phone || '').replace(/\D/g, '').slice(-10);

/**
 * Get student progress for a course
 * @param {string} studentPhone 
 * @param {string} courseId 
 * @returns {object} Progress data
 */
export const getCourseProgress = async (studentPhone, courseId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/lms/progress/${normalizeStudentPhone(studentPhone)}/${courseId}`
    );
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    // Graceful degradation - return empty progress
    return {
      success: false,
      data: {
        totalChapters: 0,
        completedChapters: 0,
        totalContent: 0,
        completedContent: 0,
        progressPercentage: 0,
        lastAccessedContent: null,
        lastAccessedAt: null,
        completedItems: [],
        timeSpentMinutes: 0
      }
    };
  }
};

/**
 * Mark content as complete
 * @param {string} studentPhone 
 * @param {string} courseId 
 * @param {number} chapterId 
 * @param {number} contentId 
 * @param {object} metadata - Additional tracking data
 * @returns {object}
 */
export const markContentComplete = async (studentPhone, courseId, chapterId, contentId, metadata = {}) => {
  try {
    const score =
      metadata?.score !== undefined && metadata?.score !== null
        ? Number(metadata.score)
        : undefined;
    const response = await axios.post(`${API_BASE_URL}/lms/progress/complete`, {
      studentPhone: normalizeStudentPhone(studentPhone),
      courseCode: courseId,
      chapterId,
      contentId,
      completedAt: new Date().toISOString(),
      ...(Number.isFinite(score) ? { score } : {}),
      metadata,
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Failed to mark content complete:', error);
    // Store in localStorage as fallback
    const key = `progress_${normalizeStudentPhone(studentPhone)}_${courseId}`;
    const stored = JSON.parse(localStorage.getItem(key) || '{"completed":[]}');
    const completionKey = `${chapterId}_${contentId}`;
    
    if (!stored.completed.includes(completionKey)) {
      stored.completed.push(completionKey);
      stored.lastUpdated = new Date().toISOString();
      localStorage.setItem(key, JSON.stringify(stored));
    }

    return {
      success: false,
      message: error.response?.data?.message || 'Failed to save progress',
      storedLocally: true
    };
  }
};

/**
 * Update last accessed content
 * @param {string} studentPhone 
 * @param {string} courseId 
 * @param {number} chapterId 
 * @param {number} contentId 
 * @returns {object}
 */
export const updateLastAccessed = async (studentPhone, courseId, chapterId, contentId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/lms/progress/last-accessed`, {
      studentPhone: normalizeStudentPhone(studentPhone),
      courseCode: courseId,
      chapterId,
      contentId,
      accessedAt: new Date().toISOString()
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    // Silently fail - not critical
    console.warn('Failed to update last accessed:', error.message);
    return {
      success: false
    };
  }
};

/**
 * Track time spent on content
 * @param {string} studentPhone 
 * @param {string} courseId 
 * @param {number} chapterId 
 * @param {number} contentId 
 * @param {number} timeSpentSeconds 
 * @returns {object}
 */
export const trackTimeSpent = async (studentPhone, courseId, chapterId, contentId, timeSpentSeconds) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/lms/progress/time-spent`, {
      studentPhone: normalizeStudentPhone(studentPhone),
      courseCode: courseId,
      chapterId,
      contentId,
      timeSpentSeconds
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Get chapter progress
 * @param {string} studentPhone 
 * @param {string} courseId 
 * @param {number} chapterId 
 * @returns {object}
 */
export const getChapterProgress = async (studentPhone, courseId, chapterId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/lms/progress/${studentPhone}/${courseId}/chapter/${chapterId}`
    );
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    return {
      success: false,
      data: {
        totalItems: 0,
        completedItems: 0,
        progressPercentage: 0
      }
    };
  }
};

/**
 * Check if content is completed (with localStorage fallback)
 * @param {string} studentPhone 
 * @param {string} courseId 
 * @param {number} chapterId 
 * @param {number} contentId 
 * @returns {boolean}
 */
export const isContentCompleted = (studentPhone, courseId, chapterId, contentId) => {
  try {
    const key = `progress_${normalizeStudentPhone(studentPhone)}_${courseId}`;
    const stored = JSON.parse(localStorage.getItem(key) || '{"completed":[]}');
    const completionKey = `${chapterId}_${contentId}`;
    return stored.completed.includes(completionKey);
  } catch {
    return false;
  }
};

/**
 * Calculate course progress percentage
 * @param {object} courseData - Course with chapters and content
 * @param {object} progressData - Student progress data
 * @returns {number} Progress percentage (0-100)
 */
export const calculateProgressPercentage = (courseData, progressData) => {
  try {
    const totalItems = courseData.chapters.reduce((sum, ch) => sum + (ch.items?.length || 0), 0);
    if (totalItems === 0) return 0;

    const completedItems = progressData.completedItems?.length || 0;
    return Math.round((completedItems / totalItems) * 100);
  } catch {
    return 0;
  }
};

export default {
  getCourseProgress,
  markContentComplete,
  updateLastAccessed,
  trackTimeSpent,
  getChapterProgress,
  isContentCompleted,
  calculateProgressPercentage
};
